import { useCallback, useState } from "react";

/**
 * A search box whose text survives navigating away and back.
 *
 * Admin work is a loop: search a list, open a case, come back, open the next one. Every table used
 * to forget the query on the way back, so the whole search had to be retyped for each result —
 * which is worst exactly when the list is long enough to need searching.
 *
 * **sessionStorage, not the URL and not localStorage.** The URL would tie the query to browser
 * history, so it would come back on Back but *not* when the same list is reached from the menu —
 * and it puts customer names in a shareable link. localStorage would persist for weeks, so a stale
 * filter from Tuesday would silently hide rows today, which reads as data missing. A session is the
 * right lifetime: it lasts as long as the person is working and ends when they close the tab.
 *
 * Clearing is explicit — that is the contract the user asked for. The × in the box is the only
 * thing that forgets a query; navigation never does.
 */
export function useStickySearch(key) {
  const storageKey = `dharmatn.search.${key}`;

  const [value, setValue] = useState(() => {
    try { return window.sessionStorage.getItem(storageKey) || ""; } catch { return ""; }
  });

  // Writes are wrapped: sessionStorage throws outright in some privacy modes, and a search box
  // that cannot remember its text is a much smaller problem than one that crashes the page.
  const set = useCallback((next) => {
    setValue(next);
    try {
      if (next) window.sessionStorage.setItem(storageKey, next);
      else window.sessionStorage.removeItem(storageKey);
    } catch { /* storage unavailable — the query just won't survive the next navigation */ }
  }, [storageKey]);

  const clear = useCallback(() => set(""), [set]);
  return [value, set, clear];
}

export default useStickySearch;
